/**
 * Platform API HTTP client.
 * Prefer AuthContext session bearer (sessionStorage); lab env token only in non-production builds.
 */
import axios from 'axios';
import { endSession } from '@/core/auth/sessionExpiry';
import { getAccessToken } from '@/core/auth/tokenStore';

const DEFAULT_PLATFORM_URL = 'http://127.0.0.1:9001';

export function getPlatformApiUrl() {
  const raw = process.env.REACT_APP_PLATFORM_API_URL || process.env.REACT_APP_BACKEND_URL;
  const fromEnv = typeof raw === 'string' ? raw.trim() : '';
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  // Same origin as the page: nginx (container) and the CRA dev proxy both forward /v1.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return DEFAULT_PLATFORM_URL;
}

export function getPlatformLabToken() {
  // Production SPA must never ship a baked lab HMAC (S-M2).
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  const raw = process.env.REACT_APP_PLATFORM_LAB_TOKEN;
  return typeof raw === 'string' ? raw.trim() : '';
}

/** Lab surfaces require an explicit token or an authenticated session. */
export function isPlatformLabEnabled() {
  return Boolean(getPlatformLabToken() || getAccessToken());
}

export function getSessionAccessToken() {
  return (getAccessToken() || '').trim();
}

export const platformClient = axios.create();

platformClient.interceptors.request.use((config) => {
  config.baseURL = getPlatformApiUrl();
  const token = getSessionAccessToken() || getPlatformLabToken();
  if (token) {
    if (config.headers && typeof config.headers.set === 'function') {
      config.headers.set('Authorization', `Bearer ${token}`);
    } else {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

platformClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    // Lab env token alone is not a browser session — only kick out real sessions.
    if (status === 401 && getSessionAccessToken()) {
      endSession('unauthorized');
    }
    return Promise.reject(error);
  },
);

function request(method, url, data, config = {}) {
  return platformClient.request({
    ...config,
    method,
    url,
    data,
  });
}

export function platformGet(url, config) {
  return platformClient.get(url, config);
}

export function platformPost(url, data, config) {
  return request('post', url, data, config);
}

export function platformPut(url, data, config) {
  return request('put', url, data, config);
}

export function platformPatch(url, data, config) {
  return request('patch', url, data, config);
}

export function platformDelete(url, config) {
  return platformClient.delete(url, config);
}
